/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { assign, createMachine } from 'xstate';

import UserApi from 'entities/user/api';
import User, { OnboardingStep } from 'entities/user/model';
import history from 'utils/history';
import { NeedHelpStep } from 'components/Flow/flows/nonprofitOnboardingNeedHelp';
import { CompanyAffiliationStep } from 'components/Flow/flows/nonprofitOnboardingCompanyAffiliation';
import { NonprofitOnboardingVerification } from 'components/Flow/flows/nonprofitOnboardingVerification';
import Organization, { NonprofitEntityType } from 'entities/organization/model';
import { DataCaptureStep } from 'components/Flow/flows/nonprofitOnboardingDataCapture';

import { DataCaptureStepType, dataCaptureStepNames } from '../getAllowedSteps';
import { PaymentStep } from '../nonprofitOnboardingPayment';

export type NonprofitOnboardingMachineEvents =
  | { type: 'SUBMIT'; companies: string[] }
  | { type: 'PREV' }
  | { type: 'NEXT' }
  | { type: 'ENROLL'; location: string }
  | { type: 'DISMISS' }
  | { type: 'NAVIGATE'; location: string }
  | { type: 'SKIP' }
  | { type: 'UPDATE_CONTEXT'; value: NonprofitOnboardingMachineContext };

export interface NonprofitOnboardingMachineContext {
  userType: User['type'];
  dismissedSteps?: Record<OnboardingStep, boolean>;
  nonprofit: {
    payPalModel: Organization['payPalModel'];
    paypalCharityType: Organization['paypalCharityType'];
    nonprofitEntityType: NonprofitEntityType;
    nonprofitDataValidatedAt: Date | undefined;
    verifiedUsers: Array<{ email: string }>;
  };
  payPalVerificationCode?: string;
  allowedSteps: OnboardingStep[];
}

export const nonprofitOnboardingMachine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QDsD2yAOAnVAzAlgC4DyyARqgIZYT7JQDEAqgAoAiAggCoCiA+gGFiAOV4ANLgG0ADAF1EoDKlhF86BSAAeiABwA2aQDodAJh3SALAGZpBgOwWArAEYANCACeiZyYC+v9zRMHAIScioaOihDOlVKABsGGXkkECUVQjVkDW0EZwsjZ2lnR0dpK0cLOxM9dy8800MATkcrErtpHSaLPQdHf0D0bDwiUgpqWnoY5DjEyWcUxWVVdVTc-MLi0vLK6trPRCsaw16mu2dnJpafOxaBkCDh0LGIyeiANzAsfAIAY0pMugGMIeBJkhp0itsmtEHofMZHE0rBYdM4KhdLHVDsVDE42noaiZHPpHHYrPdHiFRuEJlFDBhKB4ALZgZCEYGgqRyCHLQHQ0C5c5WQxWCo6cy3CwXKpYhCVIwFPSi8o2PQWKoUoZUsLjSJTBnM1ns8GpSF8nKIaomZp2NWohx2RFWJqy512Zq2EzVaznfQ6TXBEY615036oJkM5AeDi4AjxfAArIMFgAJR4ADUTUsMlkLQg4dadE6UWiXEULLL1cKmrZRbcjlZbq0A09qbq3oYwxHKFGY3GE3yOWDuabebmYfn4UWkSX0eXZWThRUmiZ8kXbD0LC3tS9aVMu5Ho7H8PHE0Cs2kx6sBbCp8XUXPMQcEBZurjPXaKk4-AEHlqg7uerRAePZHv2Z7IEkCw8jm15aLehb3qWGIVs+eg6O6y5WL0lhlFYOhbr+lIATSQGGBAAKUAIlAYIQACuWBgMmaaZiO2ZQnmqI6IYHQ2FKXqlqSspKnoCLdAU+SXHopLbiR7Z0hRhBUTR9GMUOXKLJesH8vBeRFEYTTodJJiipUji9MJTTcSi1gieqVSWLJzykR2inKbRDFMReZrjjeenSAZRmOCZX7mXYwnBbiBEuMS+H6CYhGDIGznyVMyBgJAAASYDxBgzEZt5V46esXrCglVm2kW4rFG4aE4lZTjikipJdE0TltiGaUZRA2W5cCHDpgAkgA4twPCFdpeYlNITQii46q3OYxSdMJRIemqFxFsZlztcGe7ROlWU5XlbCDQAygAsudZ0TRxE7TbN6ILVZAVFDosrBbNeI2CYuG-TNu2AR2h09cdSRsVpd1+Q9c35FUL3Le9z4osKeJOBYJktCuSL+ERqAQHAGjESlnVQDBUO6QAtLV9QtLirRoh0JhEpcPiAy5dKxJkCTk+aE4ys+bTcS4fEuGSDg6OSePJR1+2GJ83x-BBvO+bpbq4lK+HSJUzimGqjiuglzQmIZVzSY6SL+tLrZ7WRBosmyKtwbk3TOIYM3YXCKJ2EWDiVgUhjEiikso2qtzs6lwHhoefYngOqs+c7hy9GJyLnM1CXIguAfBaKdbOHCuuJX+Mu265lHUR5jFO8ViCu+7SJ6F7GG+6h9TodxRQrrr0klGibXWzuHNdUduU11NjaOCcBStD73cRWVyLYQ433h4Pcmk4YBAzLAAAWkDj-d1RLl6VwY2ZLRWB9LgnItGJBz7uO+EAA */
    id: 'nonprofitOnboarding',
    key: 'nonprofitOnboarding',
    initial: 'initial',
    schema: {
      context: {} as NonprofitOnboardingMachineContext,
      events: {} as NonprofitOnboardingMachineEvents,
      services: {} as {
        saveAffiliationService: {
          data: void;
        };
      },
    },
    // global events!
    on: {
      UPDATE_CONTEXT: {
        actions: assign({
          nonprofit: (_context, event) => event.value.nonprofit,
          allowedSteps: (_context, event) => event.value.allowedSteps,
        }),
      },
    },
    states: {
      initial: {
        always: [{ target: 'payment', cond: 'verificationCompleted' }, { target: 'verification' }],
      },
      verification: {
        on: {
          NEXT: { target: 'payment', actions: ['persistVerificationAction'] },
        },
        meta: {
          component: NonprofitOnboardingVerification,
        },
      },
      payment: {
        always: [
          {
            target: 'companyAffiliation',
            cond: 'paymentCompleted',
          },
        ],
        on: {
          NEXT: [
            {
              target: 'companyAffiliation',
              actions: ['persistPaymentAction'],
            },
          ],
        },
        meta: {
          component: PaymentStep,
        },
      },
      companyAffiliation: {
        always: [
          {
            target: '#nonprofitOnboarding.payment',
            cond: 'companyAffiliationSkipBack',
          },
          {
            target: '#nonprofitOnboarding.dataCapture',
            cond: 'companyAffiliationCompleted',
          },
        ],
        on: {
          PREV: '#nonprofitOnboarding.payment',
          NEXT: {
            target: '#nonprofitOnboarding.dataCapture',
            actions: ['persistCompanyAffiliationAction'],
          },
        },
        meta: {
          component: CompanyAffiliationStep,
        },
      },
      dataCapture: {
        always: [
          {
            target: '#nonprofitOnboarding.needHelp',
            cond: 'nonprofitDataCaptureCompleted',
          },
        ],
        on: {
          PREV: 'companyAffiliation',
          NEXT: '#nonprofitOnboarding.needHelp',
        },
        meta: {
          component: DataCaptureStep,
        },
      },
      needHelp: {
        always: [
          {
            target: '#nonprofitOnboarding.finished',
            cond: 'needHelpCompleted',
          },
        ],
        on: {
          PREV: 'dataCapture',
          NAVIGATE: {
            target: 'finished',
            actions: ['navigate', 'persistNeedHelpAction'],
          },
          DISMISS: { target: 'finished', actions: ['persistNeedHelpAction'] },
        },
        meta: {
          component: NeedHelpStep,
        },
      },
      finished: {
        type: 'final',
      },
    },
    tsTypes: {} as import('./machine.typegen').Typegen0,
  },
  {
    actions: {
      persistVerificationAction: (context): void => {
        if (!context.dismissedSteps?.verification) {
          UserApi.update({
            onboarding: {
              verification: true,
            },
          });
        }
      },
      persistCompanyAffiliationAction: (context): void => {
        if (!context.dismissedSteps?.companyAffiliation) {
          UserApi.update({
            onboarding: {
              companyAffiliation: true,
            },
          });
        }
      },
      persistPaymentAction: (context): void => {
        if (!context.dismissedSteps?.payment) {
          UserApi.update({
            onboarding: {
              payment: true,
            },
          });
        }
      },
      persistNeedHelpAction: (context): void => {
        if (!context.dismissedSteps?.needHelp) {
          UserApi.update({
            onboarding: {
              needHelp: true,
            },
          });
        }
      },
      navigate: (_context, event): void => {
        if (event.type === 'NAVIGATE') {
          history.push(event.location);
        }
      },
    },
    guards: {
      companyAffiliationSkipBack: (context, _event, { state }) =>
        !context.allowedSteps.includes('companyAffiliation') &&
        (state.event?.type as string) === 'PREV',
      companyAffiliationCompleted: (context, _event, { state }) =>
        (Boolean(context.dismissedSteps?.companyAffiliation) &&
          (state.event?.type as string) !== 'PREV') ||
        !context.allowedSteps.includes('companyAffiliation'),
      paymentCompleted: (context, _event, { state }) =>
        (Boolean(context.dismissedSteps?.payment) && (state.event?.type as string) !== 'PREV') ||
        !context.allowedSteps.includes('payment'),
      needHelpCompleted: (context, _event, { state }) =>
        (Boolean(context.dismissedSteps?.needHelp) && (state.event?.type as string) !== 'PREV') ||
        !context.allowedSteps.includes('needHelp'),
      verificationCompleted: (context, _event, { state }) =>
        (Boolean(context.dismissedSteps?.verification && !context.payPalVerificationCode) &&
          (state.event?.type as string) !== 'PREV') ||
        !context.allowedSteps.includes('verification'),
      nonprofitDataCaptureCompleted: (context) =>
        !context.allowedSteps.some((stepName) =>
          dataCaptureStepNames.includes(stepName as DataCaptureStepType)
        ),
    },
  }
);
